import { createSlice } from "@reduxjs/toolkit";

/**
 * Revision counters bumped by live socket events.
 *
 * Counters are kept twice: once per domain for listings, which show many claims
 * and want every change, and once per domain *and claim* for the panels of an
 * open claim, which must not refetch because something happened on a claim they
 * are not showing. A user now receives events for every claim they act on, so
 * the unscoped counter alone would have each open panel refetching on traffic
 * that cannot affect it.
 */
const initialState = {
    byDomain: {
        workkard: 0,
        activity: 0,
        note: 0,
        diary: 0,
        user: 0,
        organization: 0,
        party: 0,
        file: 0,
    },
    byClaim: {},
    selectedActivity: null,
};

const claimKey = (domain, workkardId) => `${domain}:${workkardId}`;

const liveUpdatesSlice = createSlice({
    name: "liveUpdates",
    initialState,
    reducers: {
        liveEntityChanged: (state, action) => {
            const domain = action.payload?.domain;
            const workkardId = action.payload?.workkardId;
            if (!domain || !Object.prototype.hasOwnProperty.call(state.byDomain, domain)) return;

            state.byDomain[domain] += 1;
            if (workkardId) {
                const key = claimKey(domain, workkardId);
                state.byClaim[key] = (state.byClaim[key] ?? 0) + 1;
            }
        },
        liveActivitySelected: (state, action) => {
            const workkardId = action.payload?.workkardId;
            const activityId = action.payload?.activityId;
            if (!workkardId || !activityId) return;

            state.selectedActivity = {
                workkardId,
                activityId,
                revision: (state.selectedActivity?.revision ?? 0) + 1,
            };
        },
        resetLiveUpdates: () => initialState,
        clearSelectedActivity: (state) => {
            state.selectedActivity = null;
        },
    },
});

export const { liveEntityChanged, liveActivitySelected, resetLiveUpdates, clearSelectedActivity } = liveUpdatesSlice.actions;

/**
 * Total revisions across `domains`. With a `workkardId` the total only counts
 * events that named that claim, which is what every component inside an open
 * claim wants; without one it counts events on any claim.
 */
export const selectLiveRevision = (domains, workkardId) => (state) =>
    domains.reduce((total, domain) => {
        if (workkardId) {
            return total + (state.liveUpdates?.byClaim?.[claimKey(domain, workkardId)] ?? 0);
        }
        return total + (state.liveUpdates?.byDomain?.[domain] ?? 0);
    }, 0);

/**
 * The activity this tab was told to switch to for `workkardId`, or null when the
 * last announcement named another claim. The stored object is returned as it is
 * held, so its reference only changes when a new announcement actually arrives
 * and a component can depend on it directly.
 */
export const selectLiveSelectedActivity = (workkardId) => (state) => {
    const selected = state.liveUpdates?.selectedActivity;
    if (!selected || !workkardId) return null;
    return String(selected.workkardId) === String(workkardId) ? selected : null;
};

export default liveUpdatesSlice.reducer;
